// Vibe artwork for events that have no uploaded cover image.
//
// WHY THIS EXISTS: `event_image` uploads are staff-gated, so only curated events have one (22 of 22
// on prod). A user-created meetup can never have one, and /e/{slug} is reachable from the iOS and
// Android share buttons — without this, every shared meetup renders a bare gradient where the app
// shows cover art. The apps map `vibe_tag` to one of 30 artworks; this is that map, for web.
//
// The images live in `public/vibe-art/` and are referenced by URL, NOT imported. They cannot go in
// `src/static/images/images/` like every other image in this app: that directory works by static
// `import`, and CRA cannot statically analyse a lookup keyed by a runtime `vibe_tag`.
//
// Page weight is unaffected — this is a lookup, so exactly one image loads per page view.

// Ported verbatim from animates-ios AMEventVibeStyle.swift `table` (30 entries).
// Keys use \u{...} escapes so they are byte-identical to what the DB stores.
// 4 keys carry an invisible U+FE0F variation selector - never retype these by hand.
//
// 🔒 VERIFIED: this object was diffed key-by-key against the Swift table on 2026-08-30 and is
// identical — 30 entries, the same 4 U+FE0F keys (PC, Gym, Motorcycle Rides, Art) and the same 7
// keys with no emoji at all (Unsure yet, TCG, Discord, Playstation, Xbox, Nintendo Switch,
// Pokemon Go). If you ever edit this, diff it against the Swift file again rather than eyeballing —
// a wrong variation selector is a silent lookup miss that no test would catch.
export const VIBE_ART = {
    "Unsure yet": "community-vibe-unsure-yet",
    "\u{1F35C} Food": "community-vibe-food",
    "\u{1F378} Drinks": "community-vibe-drinks",
    "\u{2615} Coffee": "community-vibe-coffee",
    "\u{1F9CB} Boba": "community-vibe-boba",
    "\u{1F3AE} Gaming": "community-vibe-gaming",
    "\u{1F3B2} Game Night": "community-vibe-game-night",
    "TCG": "community-vibe-tcg",
    "Discord": "community-vibe-discord",
    "Playstation": "community-vibe-playstation",
    "Xbox": "community-vibe-xbox",
    "Nintendo Switch": "community-vibe-nintendo-switch",
    "\u{1F5A5}\u{FE0F} PC": "community-vibe-pc",
    "Pokemon Go": "community-vibe-pokemon-go",
    "\u{1F4BB} Online": "community-vibe-online",
    "\u{1F3AA} Convention": "community-vibe-convention",
    "\u{1F4FA} Watch Party": "community-vibe-watch-party",
    "\u{1F3A4} Karaoke": "community-vibe-karaoke",
    "\u{1F303} Night Hang": "community-vibe-night-hang",
    "\u{1F3B8} Concert": "community-vibe-concert",
    "\u{1F49C} K-Pop": "community-vibe-k-pop",
    "\u{1F97E} Hiking": "community-vibe-hiking",
    "\u{1F3CB}\u{FE0F} Gym": "community-vibe-gym",
    "\u{1F94A} MMA": "community-vibe-mma",
    "\u{1F697} Car Meetup": "community-vibe-car-meetup",
    "\u{1F3CD}\u{FE0F} Motorcycle Rides": "community-vibe-motorcycle-rides",
    "\u{1F6B2} Bike Meet": "community-vibe-bike-meet",
    "\u{1F6B6} Outing": "community-vibe-outing",
    "\u{1F91D} Casual Hangout": "community-vibe-casual-hangout",
    "\u{270F}\u{FE0F} Art": "community-vibe-art",
};

// The unknown-vibe fallback. See the reserved-artwork rule below for why it is this and not the
// house background.
const UNKNOWN_VIBE_ASSET = "community-vibe-unsure-yet";

// 🔒 RESERVED STRICTLY FOR CURATED EVENTS — ported verbatim from AMEventVibeStyle.swift:92-97:
//
//     `AniMatesEventCardBackground` is RESERVED STRICTLY FOR CURATED EVENTS. It is the visual
//     signal that AniMates itself put the event there, so nothing else may ever fall back to it —
//     in particular the unknown-vibe path uses `community-vibe-unsure-yet`, because a regular event
//     whose vibe string drifted would otherwise masquerade as officially curated. Android was doing
//     exactly that and is being changed to match; do not reintroduce it here.
//
// It is referenced in exactly ONE place in this file (the curated branch of resolveEventArtwork).
// If you find yourself adding a second reference, you are almost certainly about to break the rule.
const CURATED_ASSET = "AniMatesEventCardBackground";

// CRA serves `public/` from the site root. PUBLIC_URL is a build-time constant that is '' while the
// app is hosted at '/', so this is just '/vibe-art/...' today — but it keeps working if a `homepage`
// is ever set in package.json, which a hardcoded path would not.
const artUrl = (asset) => process.env.PUBLIC_URL + '/vibe-art/' + asset + '.jpg';

/**
 * The single source of truth for "what image goes on this event's card/hero".
 *
 * ⚠️ Both the splash cards (PublicEvents.js) and the detail hero (EventDetail.js) call this. Do not
 * reimplement the order in either — a card and its own detail page disagreeing is the bug this
 * prevents.
 *
 * Resolution order:
 *   1. `event_image` present                      -> use it. (Every curated event today.)
 *   2. no image, `event_source === 'animates'`    -> the curated house background.
 *   3. no image, `vibe_tag` matches the table     -> that vibe's artwork.
 *   4. no image, vibe null/empty/unrecognised     -> `community-vibe-unsure-yet`.
 *
 * ⚠️ The curated check MUST come before the vibe lookup, even though the brief lists it last. Its
 * whole purpose is that a future imageless curated event does not "fall through to a vibe artwork" —
 * and curated events DO carry a vibe_tag on this endpoint (dev has "🎪 Convention", "☕ Coffee"), so
 * checking it second would never fire. This is also where web necessarily diverges from iOS: iOS
 * selects the house background by EMPTY/NULL TAGS, which would never match the web payload.
 *
 * Returns a URL string; never null, so callers always have something to render.
 */
export function resolveEventArtwork(event) {
    if (!event) return artUrl(UNKNOWN_VIBE_ASSET);
    if (event.event_image) return event.event_image;
    if (event.event_source === 'animates') return artUrl(CURATED_ASSET);
    const asset = VIBE_ART[event.vibe_tag];
    return artUrl(asset || UNKNOWN_VIBE_ASSET);
}
